import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, Plus, CalendarRange, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { hrAPI } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { ScheduleGrid } from './ScheduleGrid';
import { ShiftDialog } from './ShiftDialog';
import { WeekNav } from './WeekNav';
import { useWeekSchedule } from './useWeekSchedule';
import { PendingTimeOffBar } from './PendingTimeOffBar';
import { shiftHours, addDays, toISODate, weekLabel } from './scheduleUtils';

export default function ScheduleModule() {
  const { isAdmin } = useAuth();
  const { monday, data, loading, reload, prevWeek, nextWeek, thisWeek } = useWeekSchedule();
  const [dialog, setDialog] = useState(null); // {emp, iso} | {shift}
  const [search, setSearch] = useState('');
  const [copying, setCopying] = useState(false);

  const copyLastWeek = async () => {
    const src = addDays(monday, -7);
    if (!window.confirm(`Copy all shifts from ${weekLabel(src)} into ${weekLabel(monday)}?\nRecurring shifts already covering this week are skipped.`)) return;
    setCopying(true);
    try {
      const res = await hrAPI.copyWeek({ source_start: toISODate(src), target_start: toISODate(monday) });
      const { created, skipped, employees: n } = res.data;
      toast.success(created ? `Copied ${created} shift${created === 1 ? '' : 's'} for ${n} employee${n === 1 ? '' : 's'}${skipped ? ` (${skipped} already covered)` : ''}` : 'Nothing to copy — last week had no shifts that are not already covered');
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to copy week');
    } finally {
      setCopying(false);
    }
  };

  const employees = data?.employees || [];
  const q = search.trim().toLowerCase();
  const visibleEmployees = q ? employees.filter((e) => (e.name || '').toLowerCase().includes(q)) : employees;
  const shifts = data?.shifts || [];
  const timeOff = data?.time_off || [];
  const totalHours = shifts.reduce((a, s) => a + shiftHours(s), 0);
  const unscheduled = employees.filter((e) => !shifts.some((s) => s.employee_id === e.id)).length;

  return (
    <div className="flex-1 p-4 md:p-6 lg:p-8 overflow-y-auto" data-testid="schedule-module">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-800 mb-1 flex items-center gap-2"><CalendarRange className="w-7 h-7 text-cyan-600" /> Team Schedule</h1>
          <p className="text-sm md:text-base text-slate-500">
            {isAdmin ? 'Click any cell to add a shift; click a shift to edit or remove it. Employees are emailed on every change.' : 'Your weekly schedule as set by your manager.'}
          </p>
        </div>
        <div className="flex gap-3">
          <Stat label="Scheduled hours" value={`${totalHours % 1 ? totalHours.toFixed(1) : totalHours}h`} testId="schedule-total-hours" />
          <Stat label="Unscheduled" value={unscheduled} testId="schedule-unscheduled-count" tone={unscheduled ? 'text-amber-600' : 'text-emerald-600'} />
        </div>
      </div>

      <WeekNav
        monday={monday} onPrev={prevWeek} onNext={nextWeek} onToday={thisWeek}
        right={(
          <>
            <Input placeholder="Find employee…" value={search} onChange={(e) => setSearch(e.target.value)} className="h-10 w-44 bg-white" data-testid="schedule-search-input" />
            {isAdmin && (
              <Button variant="outline" onClick={copyLastWeek} disabled={copying} data-testid="schedule-copy-last-week-btn">
                {copying ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Copy className="w-4 h-4 mr-1" />} Copy last week
              </Button>
            )}
            {isAdmin && <Button className="bg-cyan-600 hover:bg-cyan-700 text-white" onClick={() => setDialog({ emp: null, iso: null })} data-testid="schedule-add-shift-btn">
            <Plus className="w-4 h-4 mr-1" /> Add shift
          </Button>}
          </>
        )}
      />

      {isAdmin && <PendingTimeOffBar requests={data?.pending_requests} onChanged={reload} />}

      {loading && !data ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-cyan-500" /></div>
      ) : (
        <ScheduleGrid
          monday={monday} employees={visibleEmployees} shifts={shifts} timeOff={timeOff} editable={isAdmin}
          onAdd={(emp, iso) => setDialog({ emp, iso })}
          onEditShift={(shift) => setDialog({ shift })}
        />
      )}

      {isAdmin && (
        <ShiftDialog
          open={!!dialog} onClose={() => setDialog(null)} employees={employees}
          emp={dialog?.emp} iso={dialog?.iso} shift={dialog?.shift} onSaved={reload}
        />
      )}
    </div>
  );
}

const Stat = ({ label, value, testId, tone = 'text-slate-800' }) => (
  <div className="rounded-2xl border border-slate-200 bg-white px-4 py-2 min-w-[120px]">
    <p className="text-[11px] uppercase tracking-wider text-slate-500">{label}</p>
    <p className={`text-xl font-bold ${tone}`} data-testid={testId}>{value}</p>
  </div>
);
